import * as SQLite from 'wa-sqlite';

/**
 * Тонкая обёртка над wa-sqlite: запросы, параметры, транзакции.
 *
 * Экраны и репозитории не знают ни про указатели, ни про пошаговый разбор выражений —
 * они видят обычные строки-объекты. Всё, что ниже, — единственное место в приложении,
 * которое умеет говорить с базой.
 */

export type SqlValue = string | number | null;
export type Row = Record<string, SqlValue>;

/** Обращения к базе внутри транзакции: тот же вид, что снаружи, но без очереди. */
export interface Tx {
  query<T = Row>(sql: string, params?: SqlValue[]): Promise<T[]>;
  run(sql: string, params?: SqlValue[]): Promise<void>;
}

export interface Database extends Tx {
  /**
   * Всё или ничего: порядок списка пишется целиком, и половина записанного хуже нуля.
   *
   * Внутри транзакции работать надо **через переданный [Tx]**, а не через сам объект
   * базы: запрос «снаружи» встанет в очередь за этой же транзакцией и будет ждать её
   * до бесконечности.
   */
  transaction<T>(action: (tx: Tx) => Promise<T>): Promise<T>;
  close(): Promise<void>;
}

type Api = ReturnType<typeof SQLite.Factory>;

export function wrap(sqlite3: Api, handle: number): Database {
  /*
   * Очередь обращений к базе.
   *
   * Соединение одно, и работать с ним можно только по одному запросу за раз. Экран,
   * честно запросивший дела, события, клиентов и теги через `Promise.all`, ронял
   * сборку SQLite целиком: `Aborted(RuntimeError: Unreachable code)` в асинхронной
   * ветке и «column index out of range» в обычной.
   *
   * Транзакция занимает очередь целиком и раздаёт своим запросам отдельный вход [Tx],
   * которого очередь не касается. Пока это решалось «глубиной вложенности», любой
   * посторонний запрос, начатый в тот же момент, пролезал внутрь чужой транзакции,
   * и SQLite отвечал `bad parameter or other API misuse`. Поймано на отмене удаления
   * дела 04.09.2026: карточка перечитывала события ровно тогда, когда шёл откат.
   */
  let queue: Promise<unknown> = Promise.resolve();

  const serial = <T>(action: () => Promise<T>): Promise<T> => {
    const result = queue.then(action, action);
    // Ошибка одного запроса не должна вставать в горле у следующего.
    queue = result.catch(() => undefined);

    return result;
  };

  const bind = (stmt: number, params: SqlValue[]) => {
    params.forEach((value, index) => {
      const position = index + 1;
      if (value === null) sqlite3.bind_null(stmt, position);
      else if (typeof value === 'number') sqlite3.bind_double(stmt, position, value);
      else sqlite3.bind_text(stmt, position, value);
    });
  };

  const execute = async <T>(sql: string, params: SqlValue[]): Promise<T[]> => {
    const rows: T[] = [];

    for await (const stmt of sqlite3.statements(handle, sql)) {
      bind(stmt, params);

      const columns = sqlite3.column_names(stmt);
      while ((await sqlite3.step(stmt)) === SQLite.SQLITE_ROW) {
        const values = sqlite3.row(stmt) as SqlValue[];
        const row: Row = {};
        columns.forEach((name, index) => {
          row[name] = values[index] ?? null;
        });
        rows.push(row as T);
      }
    }
    return rows;
  };

  /** Запрос с текстом SQL в ошибке: без него сбой ищется вслепую. */
  const guarded = async <T>(sql: string, params: SqlValue[]): Promise<T[]> => {
    try {
      return await execute<T>(sql, params);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(`${reason} — ${sql.trim().split(/\s+/).slice(0, 8).join(' ')}…`);
    }
  };

  /** Вход для запросов внутри транзакции: очередь уже занята ею. */
  const tx: Tx = {
    query: (sql, params = []) => guarded(sql, params),
    run: async (sql, params = []) => void (await guarded(sql, params)),
  };

  const query = <T>(sql: string, params: SqlValue[] = []): Promise<T[]> =>
    serial(() => guarded<T>(sql, params));

  return {
    query,
    run: async (sql, params = []) => void (await query(sql, params)),

    transaction<T>(action: (inside: Tx) => Promise<T>): Promise<T> {
      return serial(async () => {
        await tx.run('BEGIN');
        try {
          const result = await action(tx);
          await tx.run('COMMIT');
          return result;
        } catch (error) {
          await tx.run('ROLLBACK');
          throw error;
        }
      });
    },

    async close() {
      await sqlite3.close(handle);
    },
  };
}
